// This is some basic / boilerplate state and mutations for listing things,
// editing those things in a dialog, etc.
// Can use concurrently in multiple components, because of keyed storage

import Vue from 'vue';
// import { user } from '../../user/index';

const defaults = {
  itemIsVisible: false,
  criteria: {},
  items: [],
  item: {},
  index: -1,
  selectedTab: 0,
  isValidFormProp: { value: true },
  paginationItems: { },
  highlightedId: '',
  selectedItemsOfAllPages: [],
};

const crudHelpers = {
  ensureInit(state, property, key) {
    if (state[property][key] === undefined) {
      const def = defaults[property];
      let freshVal;
      if (Array.isArray(def)) {
        freshVal = [];
      } else if (def !== null && typeof def === 'object') {
        freshVal = {};
      } else {
        freshVal = def;
      }
      Vue.set(state[property], key, freshVal);
    }
  },
  stdMutationFunc: (property) => {
    // vuex only supports one arg to a mutation, so use [array, destructuring]
    const returnFunc = function stdMutationFunc(state, [key, newState]) {
      crudHelpers.ensureInit(state, property, key);
      state[property][key] = newState;
    };
    return returnFunc;
  },
  stdGetterFunc: (property) => {
    const returnFunc = (state) => (key) => {
      crudHelpers.ensureInit(state, property, key);
      return state[property][key];
    };
    return returnFunc;
  },
};

export const crud = {
  namespaced: true,
  // ---------------
  // actions will tend to be GQL / API specific, so we probably won't define any at the base level
  // actions will certainly make use of the base features in this file, to save time in coding
  // ---------------
  // actions: {
  // },
  // ---------------
  state: () => ({
    criteria: {}, // criteria of items listed (pagination + any custom filtering combined)
    items: {}, // list of items
    item: {}, // item (to edit, to view, whatever/etc)
    index: {}, // index of item
    isVisible: {}, // item dialog visibility
    dialogStack: [], // tracks open dialogs in order; top = most recently opened
    selectedTab: {},
    isValidFormProp: {},
    paginationItems: {},
    highlightedId: {},
    selectedItemsOfAllPages: [],
  }),
  mutations: {
    // setCriteria: crudHelpers.stdMutationFunc('criteria'),
    setCriteria(state, [key, newState, merge]) {
      crudHelpers.ensureInit(state, 'criteria', key);
      if (merge) {
        state.criteria[key] = {
          ...state.criteria[key],
          ...newState,
        };
      } else {
        state.criteria[key] = newState;
      }
    },
    setItems: crudHelpers.stdMutationFunc('items'),
    setItem: crudHelpers.stdMutationFunc('item'),
    setIndex: crudHelpers.stdMutationFunc('index'),
    setIsVisible(state, [key, newState]) {
      crudHelpers.ensureInit(state, 'isVisible', key);
      state.isVisible[key] = newState;
      if (newState) {
        if (!state.dialogStack.includes(key)) state.dialogStack.push(key);
      } else {
        const idx = state.dialogStack.lastIndexOf(key);
        if (idx !== -1) state.dialogStack.splice(idx, 1);
      }
    },
    setSelectedTab: crudHelpers.stdMutationFunc('selectedTab'),
    setIsValidFormProp: crudHelpers.stdMutationFunc('isValidFormProp'),
    setPaginationItems: crudHelpers.stdMutationFunc('paginationItems'),
    setHighlightedId: crudHelpers.stdMutationFunc('highlightedId'),
    setSelectedItemsOfAllPages: crudHelpers.stdMutationFunc('selectedItemsOfAllPages'),
  },
  getters: {
    criteria: crudHelpers.stdGetterFunc('criteria'),
    items: crudHelpers.stdGetterFunc('items'),
    item: crudHelpers.stdGetterFunc('item'),
    index: crudHelpers.stdGetterFunc('index'),
    isVisible: crudHelpers.stdGetterFunc('isVisible'),
    selectedTab: crudHelpers.stdGetterFunc('selectedTab'),
    isValidFormProp: crudHelpers.stdGetterFunc('isValidFormProp'),
    paginationItems: crudHelpers.stdGetterFunc('paginationItems'),
    highlightedId: crudHelpers.stdGetterFunc('highlightedId'),
    topDialog: (state) => (state.dialogStack.length > 0
      ? state.dialogStack[state.dialogStack.length - 1] : null),
    selectedItemsOfAllPages: crudHelpers.stdGetterFunc('selectedItemsOfAllPages'),
    skipLimitPage: (state, getters, rootState, rootGetters) => (page) => {
      const { itemsperpage } = rootGetters['user/getSettings'];
      const skip = (page - 1) * itemsperpage;
      const limit = itemsperpage;
      return {
        skip,
        limit,
      };
    },
  },
};

export default crud;
